import { invariant, isEmpty, keyBy, type Maybe } from "@banjoanton/utils";
import { createLogger } from "@chesspresso/common";
import { Chess } from "chess.js";
import { LRUCache } from "lru-cache";
import { ChessGameRepository } from "../db/chess-game-repository";
import {
    ANALYSIS_VERSION,
    ChessGame,
    type ChessPosition,
    type ChessPositionAnalysis,
    Engine,
    EngineModel,
    type EvaluatePositionResponse,
    EvaluationUtil,
    type EvaluatorBestMoveMessage,
    EvaluatorEngine,
    type EvaluatorEngineMessage,
    type EvaluatorEvaluationMessage,
    type EvaluatorMessageType,
    Move,
    type MoveAnalysis,
    MultiPvLine,
    MultiPvLineSummary,
    Position,
    PositionUtil,
} from "../index";
import { ClassifyMoveEvaluationsMapper } from "../mappers/classify-move-evaluations-mapper";
import { EvaluationDisplay } from "../models/evaluation-display";
import { AnalysisUtil } from "../utils/analysis-util";
import type { ReplayEngine } from "./replay-engine";
import { AnnotationService } from "../features/annotations/services/annotation-service";

const logger = createLogger("analysis-manager");

export type EvaluationsForPosition = {
    evaluationAfter: EvaluatePositionResponse;
    evaluationBefore: EvaluatePositionResponse;
    afterBestReplyEvaluation: EvaluatePositionResponse;
    bestLines: MultiPvLineSummary;
    previousBestLines: MultiPvLineSummary | undefined;
};

type CreateMessageCacheKeyProps = {
    gameId: string;
    fen: string;
    messageType: EvaluatorMessageType;
    engineModel: EngineModel;
    depth?: number;
};

const createMessageCacheKey = ({
    gameId,
    fen,
    messageType,
    depth = 0,
    engineModel,
}: CreateMessageCacheKeyProps) =>
    `${gameId}-${fen}-${messageType}-${depth}-${engineModel}`;

type CreateMultiPvMessageCacheKeyProps = {
    gameId: string;
    fen: string;
    engineModel: EngineModel;
    depth: number;
    variations: number;
};

const createMultiPvMessageCacheKey = ({
    fen,
    engineModel,
    gameId,
    depth,
    variations,
}: CreateMultiPvMessageCacheKeyProps) =>
    `${gameId}-${fen}-multipv-${depth}-${variations}-${engineModel}`;

export type CachedEvaluatorEngineMessage = Extract<
    EvaluatorEngineMessage,
    EvaluatorEvaluationMessage | EvaluatorBestMoveMessage
>;

type AnalysisCache = {
    position: ChessPositionAnalysis;
    move?: MoveAnalysis;
};

type PositionAnalysisCacheKeyProps = {
    gameId: string;
    fen: string;
    depth: number;
    engineModel: EngineModel;
};

const createPositionAnalysisCacheKey = ({
    gameId,
    fen,
    depth,
    engineModel,
}: PositionAnalysisCacheKeyProps) => `${gameId}-${fen}-${depth}-${engineModel}`;

type EvaluateFullPositionResult = {
    updatedPosition: ChessPosition;
    positionAnalysis: ChessPositionAnalysis;
    moveAnalysis?: MoveAnalysis;
    updatedMove?: Move;
};

type AnalysisManagerProps = {
    replayEngine: ReplayEngine;
    evaluatorEngine: EvaluatorEngine;
};

type EvaluateFullPositionProps = {
    position: ChessPosition;
    depth: number;
    // needed for annotations
    previousMoves: Move[] | undefined;
};

export class AnalysisManager {
    private replay: ReplayEngine;
    private evaluator: EvaluatorEngine;
    private analysisCache: LRUCache<string, AnalysisCache>;
    private messageCache: LRUCache<string, CachedEvaluatorEngineMessage>;
    private multiPvCache: LRUCache<string, MultiPvLineSummary>;
    private moveMap: Record<string, Move>;
    private game: ChessGame;

    constructor({ replayEngine, evaluatorEngine }: AnalysisManagerProps) {
        this.replay = replayEngine;
        this.evaluator = evaluatorEngine;
        this.game = replayEngine.getGame();
        this.analysisCache = new LRUCache<string, AnalysisCache>({ max: 1000 });
        this.multiPvCache = new LRUCache<string, MultiPvLineSummary>({
            max: 1000,
        });

        this.messageCache = new LRUCache<string, CachedEvaluatorEngineMessage>({
            max: 1000,
        });

        this.moveMap = keyBy(this.game.moves, (move) => move.id);

        // Warm cache from existing analysis
        let hasWarmedCache = false;
        for (const position of this.game.positions) {
            if (position.analysis) {
                hasWarmedCache = true;
                const key = createPositionAnalysisCacheKey({
                    gameId: this.game.id,
                    fen: position.fen,
                    depth: position.analysis.depth,
                    engineModel: evaluatorEngine.getEngineModel(),
                });
                const move = PositionUtil.getMoveForPosition(
                    position,
                    this.moveMap
                );
                const analysis: AnalysisCache = {
                    position: position.analysis,
                    move: move?.analysis,
                };
                this.analysisCache.set(key, analysis);
            }
        }
        if (hasWarmedCache) {
            logger.debug(
                { size: this.analysisCache.size, gameId: this.game.id },
                "Warmed move analysis cache"
            );
        }
    }

    private async cacheOrEvaluatePosition(depth: number, fen: string) {
        const engineModel = this.evaluator.getEngineModel();
        const bestMoveKey = createMessageCacheKey({
            gameId: this.game.id,
            fen,
            messageType: "BEST_MOVE",
            engineModel,
        });
        const evaluationKey = createMessageCacheKey({
            gameId: this.game.id,
            fen,
            messageType: "EVALUATION",
            depth,
            engineModel,
        });

        const bestMoveMsg = this.messageCache.get(
            bestMoveKey
        ) as Maybe<EvaluatorBestMoveMessage>;

        const evalMsg = this.messageCache.get(
            evaluationKey
        ) as Maybe<EvaluatorEvaluationMessage>;

        if (!bestMoveMsg || !evalMsg) {
            const response = await this.evaluator.evaluatePosition(fen, depth);
            this.messageCache.set(evaluationKey, response.evaluationMessage);
            this.messageCache.set(bestMoveKey, response.bestMoveMessage);
            const r: EvaluatePositionResponse = {
                bestMoveMessage: response.bestMoveMessage,
                evaluationMessage: response.evaluationMessage,
            };

            return r;
        }

        const response: EvaluatePositionResponse = {
            bestMoveMessage: bestMoveMsg,
            evaluationMessage: evalMsg,
        };
        return response;
    }

    async getEvaluationsForPosition(
        position: ChessPosition,
        depth: number
    ): Promise<EvaluationsForPosition> {
        const { currentFen, previousFen } =
            PositionUtil.getFenContext(position);

        const evaluationAfter = await this.cacheOrEvaluatePosition(
            depth,
            currentFen
        );

        const evaluationBefore = await this.cacheOrEvaluatePosition(
            depth,
            previousFen ?? currentFen
        );

        const afterBestReplyEvaluation = await this.evaluateAfterBestReply(
            depth,
            currentFen
        );

        const bestLines = await this.cacheOrEvaluateBestLines(currentFen);
        const previousBestLines = previousFen
            ? await this.cacheOrEvaluateBestLines(previousFen)
            : undefined;

        return {
            evaluationAfter,
            evaluationBefore,
            afterBestReplyEvaluation,
            bestLines,
            previousBestLines,
        };
    }

    async evaluateFullPosition({
        position,
        depth,
        previousMoves,
    }: EvaluateFullPositionProps): Promise<EvaluateFullPositionResult> {
        const key = createPositionAnalysisCacheKey({
            gameId: this.game.id,
            fen: position.fen,
            depth,
            engineModel: this.evaluator.getEngineModel(),
        });
        const move = PositionUtil.getMoveForPosition(position, this.moveMap);

        const cached = this.analysisCache.get(key);

        const newEngineIsBetter = Engine.compare(
            this.evaluator.getEngineModel(),
            cached?.position.engineModel ?? EngineModel.Stockfish16
        );

        if (cached && !newEngineIsBetter) {
            const updatedMove = move
                ? Move.update(move, { analysis: cached.move })
                : undefined;

            return {
                updatedPosition: Position.update(position, {
                    analysis: cached.position,
                }),
                positionAnalysis: cached.position,
                moveAnalysis: cached.move,
                updatedMove,
            };
        }

        // currently does uncached analysis on some positions
        const annotations = await AnnotationService.calculateAnnotations({
            evaluator: this.evaluator,
            move,
            previousMoves,
        });

        const evaluations = await this.getEvaluationsForPosition(
            position,
            depth
        );

        const { rating, centipawnLoss } = EvaluationUtil.classifyMove({
            game: this.game,
            positionIndex: position.index,
            annotations,
            ...ClassifyMoveEvaluationsMapper.from(evaluations),
        });

        const {
            evaluationAfter,
            evaluationBefore,
            afterBestReplyEvaluation,
            bestLines,
        } = evaluations;

        const evaluationDisplay = EvaluationDisplay.from(
            evaluationAfter.evaluationMessage.score
        );

        const engineModel = this.evaluator.getEngineModel();

        const moveAnalysis: MoveAnalysis = {
            centipawnLoss,
            rating,
            engineModel,
            depth,
            annotations,
        };

        const positionAnalysis: ChessPositionAnalysis = {
            depth,
            version: ANALYSIS_VERSION,
            ponder: evaluationAfter.bestMoveMessage.ponder,
            bestMove: evaluationAfter.bestMoveMessage.bestMove,
            scoreAfter: evaluationAfter.evaluationMessage.score,
            scoreBefore: evaluationBefore.evaluationMessage.score,
            scoreAfterBestReply:
                afterBestReplyEvaluation.evaluationMessage.score,
            engineModel,
            evaluationDisplay,
            bestLines,
        };

        const analysis: AnalysisCache = {
            position: positionAnalysis,
            move: moveAnalysis,
        };

        this.analysisCache.set(key, analysis);

        const shouldOverwrite = AnalysisUtil.shouldOverwriteAnalysis(
            position.analysis,
            engineModel,
            depth
        );

        let updatedPosition = position;
        let updatedMove = move;
        if (shouldOverwrite) {
            logger.debug("Overwriting analysis for position");
            updatedMove = move
                ? Move.update(move, { analysis: moveAnalysis })
                : undefined;

            updatedPosition = position
                ? Position.update(position, {
                      analysis: positionAnalysis,
                  })
                : position;
        }

        return {
            updatedPosition,
            positionAnalysis,
            moveAnalysis,
            updatedMove,
        };
    }

    async evaluateAfterBestReply(depth: number, currentFen: string) {
        const { bestMoveMessage } = await this.cacheOrEvaluatePosition(
            depth,
            currentFen
        );

        if (isEmpty(bestMoveMessage.bestMove)) {
            logger.debug(
                { currentFen, gameId: this.game.id },
                "No best move found, probably mate or stalemate"
            );
            return this.cacheOrEvaluatePosition(depth, currentFen);
        }

        const from = bestMoveMessage.bestMove.slice(0, 2);
        const to = bestMoveMessage.bestMove.slice(2, 4);

        const helperEngine = new Chess();
        helperEngine.load(currentFen);
        helperEngine.move({
            from,
            to,
            promotion: "q",
        });
        const updatedFen = helperEngine.fen();

        return await this.cacheOrEvaluatePosition(depth, updatedFen);
    }

    /**
     * Evaluates all positions in the game and stores their evaluations and best moves in the cache.
     * Returns an array of EvaluatePositionResponse for each move.
     */
    async evaluateFullGame(
        depth: number,
        onProgress?: (progress: number) => void
    ) {
        onProgress?.(0);
        logger.debug("Started full game analysis...");
        const startTime = Date.now();

        const positionsToUpdate: ChessPosition[] = [];
        const movesToUpdate: Move[] = [];
        const totalPositions = this.game.positions.length;
        const progressStep = 100 / totalPositions;

        onProgress?.(progressStep);

        const previousMoves: Move[] = [];

        for (let i = 0; i < this.game.positions.length; i++) {
            const position = this.game.positions[i];
            invariant(position, "Positions should not be undefined");

            const { updatedPosition, updatedMove } =
                await this.evaluateFullPosition({
                    position,
                    depth,
                    previousMoves,
                });

            if (updatedMove) {
                previousMoves.push(updatedMove);
            }

            positionsToUpdate.push(updatedPosition);

            if (updatedMove) {
                movesToUpdate.push(updatedMove);
            }

            onProgress?.(progressStep * i);
        }

        const currentGame = structuredClone(this.game);
        currentGame.positions = positionsToUpdate;
        currentGame.moves = movesToUpdate;
        this.game = currentGame;

        this.moveMap = Object.fromEntries(
            currentGame.moves.map((move) => [move.id, move])
        );

        const result = AnalysisUtil.getPlayerAnalysis(currentGame.moves);
        currentGame.white.analysis = result.white;
        currentGame.black.analysis = result.black;

        const updatedGame = ChessGame.updateHash(currentGame);

        // update it without awaiting
        ChessGameRepository.update(updatedGame.id, updatedGame).then();

        const endTime = Date.now();
        const durationSeconds = (endTime - startTime) / 1000;
        logger.debug("Full game analysis done in %d seconds", durationSeconds);
        onProgress?.(100);
    }

    async evaluateCurrentPosition(depth: number) {
        const position = this.replay.getCurrentPosition();
        return this.evaluateFullPosition({
            position,
            depth,
            previousMoves: undefined,
        });
    }

    getAnalysisForPosition(position: ChessPosition, depth: number) {
        const key = createPositionAnalysisCacheKey({
            gameId: this.game.id,
            fen: position.fen,
            depth,
            engineModel: this.evaluator.getEngineModel(),
        });
        return this.analysisCache.get(key);
    }

    getGame() {
        return this.game;
    }

    private async cacheOrEvaluateBestLines(
        fen: string,
        depthForLines = 7,
        variations = 3
    ) {
        const key = createMultiPvMessageCacheKey({
            gameId: this.game.id,
            fen,
            depth: depthForLines,
            engineModel: this.evaluator.getEngineModel(),
            variations,
        });

        const cachedSummary = this.multiPvCache.get(key);

        if (cachedSummary) {
            return cachedSummary;
        }

        const { evaluations } = await this.evaluator.evaluatePositionMultiPV(
            fen,
            depthForLines,
            variations
        );

        const byMultiPvScore = (
            a: EvaluatorEvaluationMessage,
            b: EvaluatorEvaluationMessage
        ) => {
            const multiPvA = a.multiPv ?? 0;
            const multiPvB = b.multiPv ?? 0;

            return multiPvA - multiPvB;
        };

        const maxDepth = evaluations.reduce((d, e) => Math.max(d, e.depth), 0);
        const engineModel = this.evaluator.getEngineModel();
        const lines = evaluations.sort(byMultiPvScore).map(MultiPvLine.from);

        const summary = MultiPvLineSummary.from({
            depth: maxDepth,
            engineModel,
            lines,
        });

        this.multiPvCache.set(key, summary);

        return summary;
    }
}
